import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY } from 'rxjs';
import { map, mergeMap, catchError, withLatestFrom, switchMap } from 'rxjs/operators';
import * as TasksActions from './tasks.actions';
import * as NotificationActions from '../../notifications/store/notifications.actions';
import { TasksService, ClientSelectors, AuthSelectors, TasksSelectors } from '@store';
import { Task, PatchObject, TaskComment } from '@store/models';

@Injectable()
export class TasksEffects {

    loadClientTasks$ = createEffect(() => this.actions$.pipe(
        ofType(TasksActions.LoadClientTasks),
        withLatestFrom(this.clientSelector.clientKid$),
        mergeMap(([, kid]) => this.tasksService.getClientTasks(kid).pipe(
            map(clientTasks => TasksActions.LoadClientTasksSuccess({ clientTasks }),
            catchError(() => EMPTY))
        ))
    ));

    loadNotificationTasks$ = createEffect(() => this.actions$.pipe(
        ofType(TasksActions.LoadNotificationTasks),
        mergeMap(() => this.tasksService.getNotificationTasks().pipe(
            map(notificationTasks => TasksActions.LoadNotificationTasksSuccess({ notificationTasks }),
            catchError(() => EMPTY))
        ))
    ));

    createTask$ = createEffect(() => this.actions$.pipe(
        ofType(TasksActions.CreateTask),
        withLatestFrom(this.authSelector.userIds$),
        switchMap(([action, userIds]) => {
            const newTask: Task = {
                ...action.task,
                mid: userIds.userId,
                kontor: userIds.officeId
            };
            return this.tasksService.createTask(newTask).pipe(
                mergeMap(task => {
                    const taskComment: TaskComment = {
                        taskId: task.lid,
                        created: new Date(),
                        creator: userIds.userId,
                        type: 'describe',
                        comment: newTask.beskrivelse
                    };
                    return [
                        TasksActions.CreateTaskSuccess(),
                        TasksActions.CreateTaskDescription({ taskComment })
                    ];
                })
            );
        })
    ));

    createTaskSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(TasksActions.CreateTaskSuccess),
        switchMap(() => [
            TasksActions.LoadClientTasks(),
            TasksActions.LoadNotificationTasks(),
            new NotificationActions.LoadNotifications()
        ])
    ));

    updateTask$ = createEffect(() => this.actions$.pipe(
        ofType(TasksActions.UpdateTask),
        withLatestFrom(
            this.tasksSelector.clientTasks$,
            this.tasksSelector.notificationTasks$,
            this.tasksSelector.taskDescription$,
            this.authSelector.userId$),
        switchMap(([action, clientTasks, notificationsTasks, taskDescription, userId]) => {
            const task = action.task;
            const oldValues = [...clientTasks, ...notificationsTasks].find(c => c.lid === action.task.lid);
            const patchValues: PatchObject[] = [];
            const newDescription: TaskComment = task.beskrivelse !== taskDescription
            ? {
                taskId: task.lid,
                created: new Date(),
                creator: userId,
                type: 'describe',
                comment: task.beskrivelse
            }
            : undefined;
            Object.keys(task).forEach(key => {
                if (key !== 'beskrivelse' && task[key] !== oldValues[key] && !(!task[key] && !oldValues[key])) {
                    patchValues.push({value: task[key], path: '/' + key, op: 'replace'});
                }
            });
            return this.tasksService.updateTask(patchValues, task.lid).pipe(
                switchMap(() => {
                    if (!newDescription) {
                        return [ TasksActions.UpdateTaskSuccess() ];
                    } else {
                        return [
                            TasksActions.UpdateTaskSuccess(),
                            TasksActions.CreateTaskComment({ taskComment: newDescription })
                        ];
                    }
                }
                )
            );
        })
    ));

    updateTaskSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(TasksActions.UpdateTaskSuccess),
        switchMap(() => [
            TasksActions.LoadClientTasks(),
            TasksActions.LoadNotificationTasks(),
            new NotificationActions.LoadNotifications()
        ])
    ));

    loadTaskComments$ = createEffect(() => this.actions$.pipe(
        ofType(TasksActions.LoadTaskComments),
        switchMap(action => this.tasksService.getTaskComments(action.task.lid).pipe(
            map((taskComments: TaskComment[]) => TasksActions.LoadTaskCommentsSuccess({ taskComments })),
            catchError(() => EMPTY)
        ))
    ));

    createTaskComment$ = createEffect(() => this.actions$.pipe(
        ofType(TasksActions.CreateTaskComment),
        switchMap(action => this.tasksService.createTaskComment(action.taskComment).pipe(
            map(taskComment => TasksActions.CreateTaskCommentSuccess({ taskComment })),
            catchError(() => EMPTY)
        ))
    ));

    createTaskDescription$ = createEffect(() => this.actions$.pipe(
        ofType(TasksActions.CreateTaskDescription),
        switchMap(action => this.tasksService.createTaskComment(action.taskComment))
    ), {dispatch: false} );

    constructor(
        private actions$: Actions,
        private authSelector: AuthSelectors,
        private clientSelector: ClientSelectors,
        private tasksSelector: TasksSelectors,
        private tasksService: TasksService
    ) {}

}
